<template>
  <div class="scheduled-tasks-container">
    <!-- 页面标题 -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100">定时任务管理</h1>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">查看定时任务状态并手动触发执行</p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- 左侧：任务列表 -->
      <div class="lg:col-span-2">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">定时任务列表</h2>
            <button @click="loadTasksStatus" :disabled="loadingTasks"
              class="primary-btn gap-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <font-awesome-icon :icon="['fas', 'sync-alt']" :class="{ 'animate-spin': loadingTasks }" class="mr-1" />
              刷新状态
            </button>
          </div>

          <div v-if="loadingTasks" class="p-8 text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p class="text-sm text-gray-500 mt-4">加载任务状态中...</p>
          </div>

          <div v-else-if="tasks.length === 0" class="p-8 text-center">
            <font-awesome-icon :icon="['fas', 'tasks']" class="text-6xl text-gray-300 mb-4" />
            <p class="text-gray-500">暂无定时任务</p>
          </div>

          <div v-else class="divide-y divide-gray-200 dark:divide-gray-700">
            <div v-for="task in tasks" :key="task.id"
              class="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <h3 class="text-base font-semibold text-gray-800 dark:text-gray-100">
                    {{ task.name }}
                  </h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    任务 ID: <code class="bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">{{ task.id }}</code>
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-300 mt-2">
                    <font-awesome-icon :icon="['fas', 'clock']" class="mr-1" />
                    下次执行:
                    <span class="font-medium">
                      {{ task.nextRunTime ? formatDateTime(task.nextRunTime) : '未设置' }}
                    </span>
                  </p>
                </div>

                <div class="flex flex-col gap-2 ml-4">
                  <button @click="triggerTask(task.id)" :disabled="executingTasks.has(task.id)"
                    class="primary-btn gap-1 text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                    <font-awesome-icon :icon="['fas', executingTasks.has(task.id) ? 'spinner' : 'play']"
                      :class="{ 'animate-spin': executingTasks.has(task.id) }" class="mr-1" />
                    {{ executingTasks.has(task.id) ? '执行中...' : '立即执行' }}
                  </button>
                  <button @click="viewTaskLogs(task.id)"
                    class="primary-btn gap-1 text-sm whitespace-nowrap">
                    <font-awesome-icon :icon="['fas', 'file-alt']" class="mr-1" />
                    查看日志
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：执行历史 -->
      <div class="lg:col-span-1">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div class="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">执行历史</h2>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">最近 10 次手动执行记录</p>
          </div>

          <div v-if="executionHistory.length === 0" class="p-8 text-center">
            <font-awesome-icon :icon="['fas', 'history']" class="text-4xl text-gray-300 mb-2" />
            <p class="text-sm text-gray-500">暂无执行记录</p>
          </div>

          <div v-else class="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
            <div v-for="(record, index) in executionHistory" :key="index" class="p-3">
              <div class="flex items-start justify-between">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                    {{ getTaskName(record.taskId) }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {{ formatDateTime(record.startedAt) }}
                  </p>
                  <p class="text-xs text-gray-600 dark:text-gray-300 mt-1">
                    {{ record.message }}
                  </p>
                </div>
                <span :class="record.status === 'success'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'"
                  class="px-2 py-1 text-xs font-medium rounded-full ml-2 whitespace-nowrap">
                  {{ record.status === 'success' ? '成功' : '失败' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import {
  getTasksStatusAPI,
  triggerResetQuotasAPI,
  triggerCheckExpiredSubscriptionsAPI,
  triggerSyncRedisToDbAPI,
  triggerUnlockCommissionsAPI,
} from '@/api/admin/tasks'
import type { TaskInfo, TaskExecutionResponse } from '@/types/admin/tasks'
import toaster from '@/utils/toaster'

const router = useRouter()

// 状态管理
const tasks = ref<TaskInfo[]>([])
const loadingTasks = ref(false)
const executingTasks = ref<Set<string>>(new Set())
const executionHistory = ref<TaskExecutionResponse[]>([])

/**
 * 加载任务状态
 */
const loadTasksStatus = async () => {
  loadingTasks.value = true
  try {
    const response = await getTasksStatusAPI()
    if (response.status === 200 && response.data) {
      tasks.value = response.data
    } else {
      toaster.error(response.message || '加载任务状态失败')
    }
  } catch (error) {
    console.error('加载任务状态失败:', error)
    toaster.error('加载任务状态失败')
  } finally {
    loadingTasks.value = false
  }
}

/**
 * 触发任务执行
 */
const triggerTask = async (taskId: string) => {
  executingTasks.value.add(taskId)

  try {
    let response: any

    switch (taskId) {
      case 'reset_quotas':
        response = await triggerResetQuotasAPI()
        break
      case 'check_expired_subscriptions':
        response = await triggerCheckExpiredSubscriptionsAPI()
        break
      case 'sync_redis_to_db':
        response = await triggerSyncRedisToDbAPI()
        break
      case 'unlock_commissions':
        response = await triggerUnlockCommissionsAPI()
        break
      default:
        toaster.error(`未知的任务 ID: ${taskId}`)
        return
    }

    if (response.status === 200 && response.data) {
      toaster.success(`任务执行成功: ${getTaskName(response.data.taskId)}`)

      // 添加到执行历史
      executionHistory.value.unshift(response.data)

      // 只保留最近 10 条记录
      if (executionHistory.value.length > 10) {
        executionHistory.value = executionHistory.value.slice(0, 10)
      }

      // 刷新任务状态
      await loadTasksStatus()
    } else {
      toaster.error(response.message || '任务执行失败')
    }
  } catch (error) {
    console.error('触发任务失败:', error)
    toaster.error('触发任务失败')
  } finally {
    executingTasks.value.delete(taskId)
  }
}

/**
 * 查看任务日志
 */
const viewTaskLogs = (taskId: string) => {
  // 跳转到日志页面并自动筛选该任务的日志
  router.push({
    name: 'system-logs',
    query: {
      task_id: taskId.replace(/_/g, '-')
    }
  })
}

/**
 * 格式化日期时间
 */
const formatDateTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) {
    return '未知时间'
  }

  const date = new Date(dateStr)

  // 检查日期是否有效
  if (isNaN(date.getTime())) {
    console.error('Invalid date string:', dateStr)
    return '无效时间'
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

/**
 * 根据任务 ID 获取任务名称
 */
const getTaskName = (taskId: string): string => {
  const task = tasks.value.find(t => t.id === taskId)
  return task ? task.name : taskId
}

// 生命周期钩子
onMounted(() => {
  loadTasksStatus()
})
</script>

<style scoped>
.scheduled-tasks-container {
  @apply p-6;
}

/* 自定义滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  @apply bg-gray-100 dark:bg-gray-700;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  @apply bg-gray-300 dark:bg-gray-600 rounded;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400 dark:bg-gray-500;
}
</style>
